import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BlastRow, ColorEntry, DrillingSession, ShiftConfig } from './models/drilling.models';
import { DayGroup, ShiftColumn, generateColumns, generateDayGroups } from './utils/column-generator';
import { DrillBlockCellComponent } from './drill-block-cell/drill-block-cell.component';
import { DrillChartControlsComponent, TimeScale } from './drill-chart-controls/drill-chart-controls.component';
import { distinctHslColor } from 'src/app/utils/color.utils';


@Component({
  selector: 'drill-activity-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './drill-activity-chart.component.html',
  styleUrls: ['./drill-activity-chart.component.scss'],
  imports: [DrillBlockCellComponent, DrillChartControlsComponent, TranslateModule],
})
export class DrillActivityChartComponent {

  // ---- Inputs ----------------------------------------------------------------

  readonly blasts      = input<BlastRow[]>([]);
  readonly shiftConfig = input.required<ShiftConfig>();
  readonly startDate   = input.required<Date>();
  readonly endDate     = input.required<Date>();
  readonly timeScale   = input<TimeScale>('week');
  /** Initial color-by mode; can be overridden via the controls. */
  readonly colorBy     = input<'rig' | 'operator'>('rig');
  /**
   * Explicit color palette; when omitted the chart assigns default colors.
   * Each entry maps a rigId (or operatorId) → hex color.
   */
  readonly palette     = input<ColorEntry[]>([]);

  // ---- Outputs ---------------------------------------------------------------

  readonly sessionClick    = output<DrillingSession>();
  readonly blastClick      = output<BlastRow>();
  /** Emitted when the time-scale control changes the displayed window. */
  readonly dateRangeChange = output<{ start: Date; end: Date }>();

  // ---- Internal state --------------------------------------------------------

  protected readonly activeColorBy   = signal<'rig' | 'operator'>('rig');
  protected readonly activeTimeScale = signal<TimeScale>('week');

  /** Effective date range driving column generation — initialised from inputs,
   *  then overridden locally when the user switches the time scale. */
  private readonly _displayStart = signal<Date | null>(null);
  private readonly _displayEnd   = signal<Date | null>(null);

  constructor() {
    // Mirror the colorBy input into the mutable internal signal so parent can
    // set an initial value while still allowing the controls to override it.
    effect(() => {
      this.activeColorBy.set(this.colorBy());
    });

    effect(() => {
      this.activeTimeScale.set(this.timeScale());
    });

    // Sync display dates from parent inputs (initial load and parent-driven changes).
    // These only update when the parent signals change, not when we set _displayStart/End
    // internally on a time-scale switch.
    effect(() => { this._displayStart.set(this.startDate()); });
    effect(() => { this._displayEnd.set(this.endDate()); });
  }

  // ---- Computed columns ------------------------------------------------------

  protected readonly shiftColumns = computed<ShiftColumn[]>(() =>
    generateColumns(
      this._displayStart() ?? this.startDate(),
      this._displayEnd()   ?? this.endDate(),
      this.shiftConfig(),
    )
  );

  protected readonly dayGroups = computed<DayGroup[]>(() =>
    generateDayGroups(this.shiftColumns(), this.activeTimeScale())
  );

  /** Cell width in px — shrinks automatically for wider time ranges. */
  protected readonly cellWidthPx = computed<number>(() =>
    this.activeTimeScale() === 'month' ? 32 : 56
  );

  /** Month + year label shown in the top-left corner cell in month mode. */
  protected readonly displayMonthLabel = computed<string>(() => {
    const d = this._displayStart() ?? this.startDate();
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  });

  // ---- Cell helpers ----------------------------------------------------------

  protected getSessionsForCell(blast: BlastRow, col: ShiftColumn): DrillingSession[] {
    return blast.sessions.filter(
      s => s.date === col.date && s.shiftIndex === col.shiftIndex,
    );
  }

  /**
   * The blast-date marker (§6) appears in the FIRST shift column (shiftIndex 0)
   * of the blast's blastDate.
   */
  protected isBlastDateCell(blast: BlastRow, col: ShiftColumn): boolean {
    return !!blast.blastDate && blast.blastDate === col.date && col.shiftIndex === 0;
  }

  // ---- Réalisé totals --------------------------------------------------------

  /**
   * Les mètres forés sont cumulés en virgule flottante : un total pourtant exact
   * comme 4564,4 s'affiche 4564.400000000001 s'il n'est pas arrondi. Une décimale,
   * comme les valeurs de session affichées dans les cellules.
   */
  private static roundMeters(value: number): number {
    return Math.round(value * 10) / 10;
  }

  protected getShiftTotal(col: ShiftColumn): number {
    return DrillActivityChartComponent.roundMeters(this.blasts().reduce(
      (sum, blast) =>
        sum + blast.sessions
          .filter(s => s.date === col.date && s.shiftIndex === col.shiftIndex)
          .reduce((s2, s) => s2 + s.drilledMeters, 0),
      0,
    ));
  }

  // ---- Right totals panel helpers (§8) ---------------------------------------

  protected getBlastRealized(blast: BlastRow): number {
    return DrillActivityChartComponent.roundMeters(
      blast.sessions.reduce((sum, s) => sum + s.drilledMeters, 0),
    );
  }

  protected getBlastProgress(blast: BlastRow): number {
    if (!blast.targetMeters) { return 0; }
    return (this.getBlastRealized(blast) / blast.targetMeters) * 100;
  }

  /** Clamps a percentage to [0, 100] for the CSS progress-bar width. */
  protected clamp100(value: number): number {
    return Math.min(Math.max(value, 0), 100);
  }

  protected readonly grandTotal = computed<number>(() =>
    DrillActivityChartComponent.roundMeters(this.blasts().reduce(
      (sum, blast) => sum + blast.sessions.reduce((s2, s) => s2 + s.drilledMeters, 0),
      0,
    )),
  );

  /**
   * Unique rig (or operator) entries with their resolved color for the legend.
   * Colors not explicitly overridden via `palette` are spaced evenly around the hue
   * wheel so they stay distinguishable regardless of how many entries are shown.
   */
  protected readonly legend = computed<ColorEntry[]>(() => {
    const colorBy = this.activeColorBy();
    const seen    = new Map<string, ColorEntry>();
    for (const blast of this.blasts()) {
      for (const s of blast.sessions) {
        const id    = colorBy === 'rig' ? s.equipmentId : s.operatorId;
        const label = colorBy === 'rig' ? s.equipmentLabel : s.operatorLabel;
        const key   = label.toLowerCase();
        if (!label || seen.has(key)) { continue; }
        const paletteColor = this.palette().find(e => e.id === id)?.color;
        seen.set(key, { id, label, color: paletteColor ?? '' });
      }
    }
    const entries = [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
    entries.forEach((entry, index) => {
      if (!entry.color) { entry.color = distinctHslColor(index, entries.length); }
    });
    return entries;
  });

  // ---- Controls event handlers -----------------------------------------------

  protected onColorByChange(value: 'rig' | 'operator'): void {
    this.activeColorBy.set(value);
  }

  protected onTimeScaleChange(scale: TimeScale): void {
    this.activeTimeScale.set(scale);
    const anchor = this._displayStart() ?? this.startDate();
    const range = this.computeDateRange(scale, anchor);
    this._displayStart.set(range.start);
    this._displayEnd.set(range.end);
    this.dateRangeChange.emit(range);
    if (scale === 'month') {
      setTimeout(() => this.scrollToToday());
    }
  }

  protected onNavigate(direction: -1 | 1): void {
    const start = this._displayStart() ?? this.startDate();
    const end   = this._displayEnd()   ?? this.endDate();
    let newStart: Date;
    let newEnd: Date;

    if (this.activeTimeScale() === 'week') {
      newStart = new Date(start);
      newStart.setDate(newStart.getDate() + direction * 7);
      newEnd = new Date(end);
      newEnd.setDate(newEnd.getDate() + direction * 7);
    } else {
      newStart = new Date(start.getFullYear(), start.getMonth() + direction, 1);
      newEnd   = new Date(newStart.getFullYear(), newStart.getMonth() + 1, 0);
    }

    this._displayStart.set(newStart);
    this._displayEnd.set(newEnd);
    this.dateRangeChange.emit({ start: newStart, end: newEnd });
  }

  private readonly el = inject(ElementRef);

  /** Scrolls the grid horizontally so that today's column is centred in the viewport. */
  private scrollToToday(): void {
    const host    = this.el.nativeElement as HTMLElement;
    const wrapper = host.querySelector('.dac-scroll-wrapper') as HTMLElement | null;
    if (!wrapper) { return; }

    // Target the first today data-cell in tbody (avoiding sticky thead offsets)
    const todayCell = wrapper.querySelector('tbody td.is-today') as HTMLElement | null;
    if (!todayCell) { return; }

    // --sidebar-width CSS var (fallback to 150)
    const sidebarWidth = parseFloat(getComputedStyle(host).getPropertyValue('--sidebar-width')) || 150;
    const visibleWidth = wrapper.clientWidth - sidebarWidth;
    const scrollLeft   = todayCell.offsetLeft - sidebarWidth - visibleWidth / 2 + todayCell.offsetWidth / 2;

    wrapper.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }

  private computeDateRange(scale: TimeScale, anchor: Date): { start: Date; end: Date } {
    const y   = anchor.getFullYear();
    const mo  = anchor.getMonth();
    const d   = anchor.getDate();
    const dow = anchor.getDay(); // 0 = Sun

    switch (scale) {
      case 'week': {
        const offset = dow === 0 ? -6 : 1 - dow; // align to Monday
        return {
          start: new Date(y, mo, d + offset),
          end:   new Date(y, mo, d + offset + 6),
        };
      }

      case 'month':
        return { start: new Date(y, mo, 1), end: new Date(y, mo + 1, 0) };
    }
  }
}
